/*
    Here we store functions and types common to both client and server.
*/

export type RequestError = 'bad_request' | 'too_many_requests' | 'unauthorized' | 'not_found' | 'internal_error' 
    | AccountCreationError | EmailVerificationError | SigninError | ForgotPasswordError 
    | ResetPasswordError | UpdatePasswordError | UpdateProfileError | UserChangeError 
    | GuideUploadError | GuideCleanupError | GuideHtmlConvertError | GuideValidationError;

export type EmailVerificationError = 'bad_request' | 'invalid_token' | 'already_verified' | 'internal_error';
export type SigninError = 'invalid_credentials' | 'unverified_email' | 'internal_error' | 'too_many_requests' | 'no_email_provided' | 'no_password_provided';
export type ForgotPasswordError = 'bad_request' | 'invalid_email' | 'user_not_found' | 'unverified_email' | 'internal_error' | 'too_many_requests';
export type ResetPasswordError = 'bad_request' | 'invalid_token' | 'expired_token' | 'invalid_password' | 'internal_error' | 'too_many_requests';
export type UpdateProfileError = 'invalid_email' | 'invalid_username' | 'internal_error' | 'already_assigned_email' | 'too_many_requests';
export type UpdatePasswordError = 'new_passwords_dont_match' | 'incorrect_current_password' | 'invalid_password' | 'internal_error' | 'too_many_requests' | WeakPasswordReason;
export type AccountCreationError = 'already_created' | 'invalid_email' | 'invalid_username' | 'invalid_password' | 'internal_error' | 'too_many_requests' | WeakPasswordReason;
export type WeakPasswordReason = 'pwd_too_short' | 'pwd_too_long' | 'pwd_no_letters' | 'pwd_no_numbers';
export type UserChangeError = 'user_not_found' | 'bad_request' | 'internal_error' | 'cannot_remove_last_admin' | 'already_at_target_tier';
export type GuideUploadError = 'guide_operation_already_in_progress' | 'no_zip_selected' | 'zip_empty' | 'not_a_zip' | 'zip_too_large' | 'invalid_zip_structure' | 'internal_error';
export type GuideCleanupError = 'guide_operation_already_in_progress' | 'could_not_save_zip' | 'outline_page_not_found' | 'outline_page_bad_format' | 'internal_error';
export type GuideHtmlConvertError = 'guide_operation_already_in_progress' | 'outline_file_not_found' | 'page_template_not_found' | 'internal_error';
export type GuideValidationError = 'guide_operation_already_in_progress' | 'could_not_override_live_guide' | 'no_pending_preview' | 'internal_error';

export type RequestResult<ErrorType extends RequestError = RequestError> =
    | { success: true }
    | { success: false; error: ErrorType };

// * MARK: Users
// ----------------------------------------------------------------------------

export const PWD_MIN_LENGTH = 8;
export const PWD_MAX_LENGTH = 128;

export function checkAccountCreationFields(email: any, username: any, password: any): { valid: boolean, error?: AccountCreationError } {
    if (typeof email !== 'string' || !checkValidEmail(email)) return { valid: false, error: 'invalid_email' };
    if (typeof username !== 'string' || username.length < 1 || username.length > 100) return { valid: false, error: 'invalid_username' };
    if (typeof password !== 'string') return { valid: false, error: 'invalid_password' };
    const pwdCheck = checkValidPassword(password);
    if (!pwdCheck.valid) return { valid: false, error: pwdCheck.reason };
    return { valid: true };
}

export function checkValidPassword(password: string): { valid: true } | { valid: false, reason: WeakPasswordReason } {
    if (password.length < PWD_MIN_LENGTH) return { valid: false, reason: 'pwd_too_short' };
    if (password.length > PWD_MAX_LENGTH) return { valid: false, reason: 'pwd_too_long' };
    if (!/[a-zA-Z]/.test(password)) return { valid: false, reason: 'pwd_no_letters' };
    if (!/[0-9]/.test(password)) return { valid: false, reason: 'pwd_no_numbers' };
    return { valid: true };
}

export function checkValidEmail(email: any) {
    if (typeof email !== 'string') return false;
    const emailRegex = /^(([^<>()\[\]\\.,;:\s@"]+(\.[^<>()\[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
    return emailRegex.test(email);
}

// * MARK: Guide Options
// ----------------------------------------------------------------------------

export interface GuideOptions {
    firstPaidPageUrl: string;
    discordButtonEnabled: boolean;
    discordButtonUrl: string;
    discordButtonText: string;
    discordButtonSubtext: string;
}

export function getGuidePageIdFromUrl(url: string): string {
    if (url.length === 32 && /^[A-Fa-f0-9]{32}$/.test(url)) {
        return url; // Already a page ID
    }
    url = url.replace(/\.[^/.]+$/, ""); // Remove file extension if any
    const parts = url.split('-'); // Split by hyphen
    const pageLimitId = parts.pop() || ''; // Get last part as page ID
    return pageLimitId;
}

// * MARK: Error Messages
// ----------------------------------------------------------------------------

// Get the pretty error message in french language
export function getPrettyErrorMessage(error: RequestError): string {
    try {
        const errorMessages: { [key in RequestError]: string } = {
            // General errors
            'bad_request': "Requête invalide.",
            'too_many_requests': "Trop de requêtes successives. Veuillez réessayer plus tard.",
            'unauthorized': "Vous n'êtes pas autorisé à effectuer cette action.",
            'not_found': "Ressource non trouvée.",
            'internal_error': "Une erreur interne est survenue, veuillez réessayer plus tard.",
            
            // Account creation errors
            'already_created': "Un compte avec cet e-mail ou nom d'utilisateur existe déjà.",
            'invalid_email': "L'adresse e-mail fournie est invalide.",
            'invalid_username': "Le nom d'utilisateur fourni est invalide.",
            'invalid_password': "Le mot de passe fourni est invalide.",
            'pwd_too_short': `Le mot de passe est trop court. Il doit contenir au moins ${PWD_MIN_LENGTH} caractères.`,
            'pwd_too_long': `Le mot de passe est trop long. Il doit contenir au maximum ${PWD_MAX_LENGTH} caractères.`,
            'pwd_no_letters': "Le mot de passe doit contenir au moins une lettre.",
            'pwd_no_numbers': "Le mot de passe doit contenir au moins un chiffre.",

            // Email verification errors
            'invalid_token': "Le jeton de vérification est invalide.",
            'already_verified': "L'adresse e-mail a déjà été vérifiée.",

            // Sign-in errors
            'invalid_credentials': "Identifiants invalides.",
            'unverified_email': "L'adresse e-mail n'a pas encore été vérifiée. Veuillez vérifier votre boîte de réception.",
            'no_email_provided': "Veuillez entrer votre adresse e-mail.",
            'no_password_provided': "Veuillez entrer votre mot de passe.",

            // Forgot password errors
            'user_not_found': "Aucun utilisateur trouvé avec cet e-mail.",

            // Reset password errors
            'expired_token': "Votre lien de réinitialisation du mot de passe a expiré.",

            // Update password/profile errors
            'already_assigned_email': "L'adresse e-mail est déjà associée à un autre compte.",
            'incorrect_current_password': "Le mot de passe actuel est incorrect.",
            'new_passwords_dont_match': "Les nouveaux mots de passe ne correspondent pas.",

            // User change errors
            'cannot_remove_last_admin': "Impossible de supprimer le dernier administrateur.",
            'already_at_target_tier': "L'utilisateur a déjà le niveau d'abonnement cible.",

            // Guide upload errors
            'no_zip_selected': "Veuillez sélectionner un fichier ZIP à télécharger.",
            'not_a_zip': "Le fichier n'est pas un fichier ZIP valide.",
            'zip_too_large': "Le fichier ZIP téléchargé est trop volumineux.",
            'invalid_zip_structure': "La fichier ZIP ne contient pas la structure attendue.",
            'zip_empty': "Le fichier ZIP est vide.",
            'guide_operation_already_in_progress': "Une autre opération de mise à jour du guide est déjà en cours. Veuillez réessayer plus tard.",

            // Guide cleanup errors
            'outline_page_not_found': "La page de sommaire du guide est introuvable.",
            'outline_page_bad_format': "La page de sommaire du guide n'est pas au format attendu.",
            'could_not_save_zip': "Une erreur est survenue lors de l'enregistrement du fichier ZIP nettoyé.",

            // Guide HTML convert errors
            'outline_file_not_found': "Le sommaire du guide (_outline.md) est introuvable.",
            'page_template_not_found': "Le template de page du guide est introuvable.",

            // Guide validation errors
            'no_pending_preview': "Il n'y a pas de version de prévisualisation en attente pour ce guide.",
            'could_not_override_live_guide': "Une erreur est survenue lors de la mise à jour du guide en ligne (impossible d'écraser les fichiers existants).",
        };

        return errorMessages[error] || "Une erreur inconnue est survenue. Veuillez réessayer plus tard.";
    } catch {
        return "Une erreur inconnue est survenue. Veuillez réessayer plus tard.";
    }
}



    